import { useEffect, useState } from "react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { Smartphone, BellRing, Loader2, Send, Users, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { timeAgo } from "@/lib/timeAgo";

const PRESETS = [
  ["install", "„App installieren +50“ senden", Smartphone],
  ["push", "„Push aktivieren“ senden", BellRing],
];
const AUDIENCES = [["all", "Alle Mitglieder"], ["noapp", "Nur ohne App"], ["nopush", "Nur ohne Push"]];
const PAGES = [
  ["/feed", "Feed"], ["/spiele", "Spiele"], ["/spiele/tresor", "Goldener Tresor"], ["/tasse", "Dankeschön-Tasse"], ["/geschenke", "Prämien & Geschenke"], ["/einladen", "Freunde einladen"],
  ["/community-unterstuetzen", "Unterstützer werden"], ["/app", "App installieren"], ["/push", "Push aktivieren"], ["/heimlicher-chat", "Heimlicher Chat"],
  ["/gemeinsam-punkte-sammeln", "WhatsApp-Gruppe (Gemeinsam Punkte sammeln)"],
];
const TEMPLATES = [
  ["whatsapp", "WhatsApp-Gruppe vorstellen", { text: "💚 Neu: Unsere WhatsApp-Gruppe! Sammelt gemeinsam Punkte, teilt Tipps und bekommt schnell Hilfe. Beim ersten Besuch gibt's +25 Punkte.", cta: "Gruppe ansehen", url: "/gemeinsam-punkte-sammeln" }],
];
const inp = "w-full h-11 px-4 rounded-xl border border-amber-200 bg-white text-sm outline-none focus:ring-2 focus:ring-amber-300";

export function AdminNudgeButtons() {
  const [busy, setBusy] = useState(null);
  const [aud, setAud] = useState("all");
  const [push, setPush] = useState(false);
  const [stats, setStats] = useState([]);
  const [f, setF] = useState({ text: "", cta: "", url: "/feed" });
  const loadStats = () => api.get("/admin/nudge-campaigns").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); const t = setInterval(loadStats, 30000); return () => clearInterval(t); }, []);
  const audLabel = AUDIENCES.find((a) => a[0] === aud)[1];
  const run = async (key, label, call) => {
    if (!window.confirm(`${label} – an „${audLabel}“ als goldene Mitteilung${push ? " + Push" : ""} senden?`)) return;
    setBusy(key);
    try { const { data } = await call(); toast.success(`An ${data.sent} Mitglieder gesendet ✨`); if (key === "custom") setF({ text: "", cta: "", url: "/feed" }); loadStats(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };
  return (
    <div className="w-full rounded-3xl bg-gradient-to-br from-[#fff8e1] to-[#f6e3a1]/60 border border-amber-200 p-4 space-y-3 text-[#4a2a08]" data-testid="admin-nudge-panel">
      <p className="font-extrabold flex items-center gap-2"><Send className="w-4 h-4" /> Goldene Mitteilung an Mitglieder</p>
      <div className="flex flex-wrap gap-1.5" data-testid="admin-nudge-audience">
        <Users className="w-4 h-4 mt-2 shrink-0" />
        {AUDIENCES.map(([k, l]) => (
          <button key={k} onClick={() => setAud(k)} data-testid={`admin-nudge-aud-${k}`} className={`px-3 h-8 rounded-full text-xs font-bold transition-colors ${aud === k ? "bg-[#7a1c3a] text-white" : "bg-white/80 text-[#4a2a08] hover:bg-white"}`}>{l}</button>
        ))}
      </div>
      <label className="flex items-center gap-2.5 text-sm font-bold cursor-pointer" data-testid="admin-nudge-push-toggle">
        <Switch checked={push} onCheckedChange={setPush} data-testid="admin-nudge-push-switch" /> <BellRing className="w-4 h-4" /> Zusätzlich als Push-Nachricht senden
      </label>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(([what, label, Icon]) => (
          <button key={what} onClick={() => run(what, label, () => api.post(`/admin/nudge/${what}?audience=${aud}&push=${push}`))} disabled={!!busy} data-testid={`admin-nudge-${what}`}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-gradient-to-r from-[#f6e3a1] to-[#c99a3c] text-sm font-extrabold shadow-sm active:scale-95 transition-transform disabled:opacity-60">
            {busy === what ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
          </button>
        ))}
      </div>
      <div className="space-y-2 pt-2 border-t border-amber-200" data-testid="admin-nudge-custom">
        <p className="text-sm font-bold">Eigene Mitteilung</p>
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-nudge-templates">
          <span className="text-xs font-bold text-[#7a5a2a]">Vorlage:</span>
          {TEMPLATES.map(([id, label, v]) => (
            <button key={id} type="button" onClick={() => { setF(v); toast.success("Vorlage eingefügt – du kannst den Text noch anpassen"); }} data-testid={`admin-nudge-template-${id}`}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full wa-btn text-white text-xs font-extrabold"><WhatsAppIcon className="w-3.5 h-3.5" /> {label}</button>
          ))}
        </div>
        <textarea value={f.text} maxLength={200} rows={3} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="z. B. ✨ Heute Abend Goldregen um 20 Uhr – sei dabei!" data-testid="admin-nudge-custom-text" className={`${inp} h-auto py-2.5 resize-none`} />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.cta} maxLength={30} onChange={(e) => setF({ ...f, cta: e.target.value })} placeholder="Knopf-Text, z. B. Mitmachen" data-testid="admin-nudge-custom-cta" className={inp} />
          <select value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="admin-nudge-custom-url" className={inp}>
            {PAGES.map(([to, l]) => <option key={to} value={to}>{l}</option>)}
          </select>
        </div>
        <button onClick={() => run("custom", "Eigene Mitteilung", () => api.post("/admin/nudge-custom", { ...f, audience: aud, push }))} disabled={!!busy || f.text.trim().length < 3 || f.cta.trim().length < 2} data-testid="admin-nudge-custom-send"
          className="w-full h-11 rounded-full bg-gradient-to-r from-[#7a1c3a] to-[#b3164f] text-white text-sm font-extrabold shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy === "custom" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Gold-Mitteilung senden
        </button>
      </div>
      {stats.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-amber-200" data-testid="admin-nudge-stats">
          <p className="text-sm font-bold flex items-center gap-1.5"><MousePointerClick className="w-4 h-4" /> Gesendet & angetippt</p>
          {stats.map((c) => {
            const pct = c.sent ? Math.round((c.clicks / c.sent) * 100) : 0;
            return (
              <div key={c.ref} className="rounded-2xl bg-white/80 p-3" data-testid={`admin-nudge-stat-${c.ref}`}>
                <p className="text-xs font-semibold line-clamp-2">{c.text}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] font-bold text-[#7a5a2a]">
                  <span>{timeAgo(c.created_at)}</span>·<span>{AUDIENCES.find((a) => a[0] === c.audience)?.[1] || "Alle"}</span>{c.push && <>·<span>+ Push</span></>}
                  <span className="w-full sm:w-auto sm:ml-auto text-[#7a1c3a] text-sm font-extrabold tabular-nums" data-testid={`admin-nudge-clicks-${c.ref}`}>{c.clicks} von {c.sent} angetippt · {pct} %</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-amber-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#c99a3c] to-[#7a1c3a]" style={{ width: `${Math.max(pct, 2)}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
